import { yupResolver } from "@hookform/resolvers/yup";
import { ArrowBack } from "@mui/icons-material";
import SaveIcon from "@mui/icons-material/Save";
import { LoadingButton } from "@mui/lab";
import {
    Box,
    Button,
    Card,
    Chip,
    Container,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Typography,
    alpha,
    useTheme,
} from "@mui/material";
import Iconify from "components/Iconify";
import Page from "components/Page";
import { FormProvider, RHFTextField } from "components/hook-form";
import { useCreatePromptMutation, useUpdatePromptMutation } from "hooks/prompts/usePromptsQueries";
import useSettings from "hooks/useSettings";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router";
import * as Yup from "yup";

import { AVAILABLE_TAGS } from "@/pages/dashboard/prompts/constants/prompts";
import { prepareCreatePromptData, prepareUpdatePromptData } from "@/pages/dashboard/prompts/services/prompts.service";

import { compareTagArrays, getDisplayTags, getTagColor } from "../utils/promptsUtils";

const PromptsForm = () => {
    const { t } = useTranslation();
    const { themeStretch } = useSettings();
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const theme = useTheme();

    const isEdit = Boolean(id);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedTags, setSelectedTags] = useState([]);
    const [initialTags, setInitialTags] = useState([]);
    const [tagMenuAnchor, setTagMenuAnchor] = useState(null);

    // Get prompt data from navigation state (passed from list page)
    const promptFromState = location.state?.prompt;

    // API Hooks
    const createPromptMutation = useCreatePromptMutation();
    const updatePromptMutation = useUpdatePromptMutation();

    // Validation schema
    const PromptSchema = Yup.object().shape({
        name: Yup.string()
            .trim()
            .required(t("prompt_name_is_required"))
            .min(3, t("prompt_name_must_be_at_least_3_characters"))
            .max(100, t("prompt_name_must_not_exceed_100_characters"))
            .matches(
                /^[a-zA-Z0-9\s\-_]+$/,
                t("prompt_name_can_only_contain_letters_numbers_spaces_hyphens_and_underscores")
            ),
        prompt: Yup.string()
            .trim()
            .required(t("prompt_content_is_required"))
            .min(10, t("prompt_content_must_be_at_least_10_characters"))
            .max(5000, t("prompt_content_must_not_exceed_5000_characters")),
    });

    // Default values
    const defaultValues = {
        name: "",
        prompt: "",
    };

    const methods = useForm({
        resolver: yupResolver(PromptSchema),
        defaultValues,
    });

    const {
        handleSubmit,
        reset,
        formState: { isSubmitting, isDirty },
        watch,
    } = methods;

    // Check if tags have changed
    const tagsChanged = !compareTagArrays(selectedTags, initialTags);
    const hasUnsavedChanges = isDirty || tagsChanged;

    const watchedContent = watch("prompt");

    // Load existing prompt data if editing
    useEffect(() => {
        if (isEdit && promptFromState) {
            const tags = promptFromState.tags || [];
            setSelectedTags(tags);
            setInitialTags(tags);
            reset({
                name: promptFromState.name,
                prompt: promptFromState.prompt,
            });
        } else {
            // For new prompts, set initial tags as empty
            setInitialTags([]);
        }
    }, [promptFromState, isEdit, reset]);

    const onSubmit = async (data) => {
        try {
            setIsLoading(true);

            // Tag logic: Always respect user's selection (can be empty for both edit and new prompts)
            const formDataWithTags = { ...data, tags: selectedTags };

            if (isEdit && id) {
                // Update existing prompt
                const updateData = prepareUpdatePromptData(formDataWithTags);
                await updatePromptMutation.mutateAsync({ id, data: updateData });
            } else {
                // Create new prompt
                const createData = prepareCreatePromptData(formDataWithTags);
                await createPromptMutation.mutateAsync(createData);
            }

            navigate(-1); // Go back to the list
        } catch (error) {
            console.error("Error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackClick = () => {
        navigate(-1);
    };

    const handleTagToggle = (tag) => {
        setSelectedTags((prev) => {
            if (prev.includes(tag)) {
                return prev.filter((t) => t !== tag);
            } else {
                return [...prev, tag];
            }
        });
    };

    const handleTagEditClick = (event) => {
        setTagMenuAnchor(event.currentTarget);
    };

    const handleTagMenuClose = () => {
        setTagMenuAnchor(null);
    };

    return (
        <Page title={isEdit ? "Edit Prompt" : "New Prompt"}>
            <Container maxWidth={themeStretch ? false : "lg"}>
                {/* Header Section */}
                <Card
                    sx={{
                        mb: 3,
                        borderRadius: 3,
                        boxShadow: `0 4px 20px ${alpha(theme.palette.grey[500], 0.1)}`,
                        border: `1px solid ${alpha(theme.palette.grey[500], 0.08)}`,
                    }}
                >
                    <Box sx={{ p: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <IconButton
                                onClick={handleBackClick}
                                sx={{
                                    backgroundColor: alpha(theme.palette.grey[500], 0.1),
                                    "&:hover": {
                                        backgroundColor: alpha(theme.palette.grey[500], 0.2),
                                    },
                                }}
                            >
                                <ArrowBack />
                            </IconButton>
                            <Box sx={{ flex: 1 }}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                                        {isEdit ? "Edit Prompt" : "New Prompt"}
                                    </Typography>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        {selectedTags.length > 0 && (
                                            <Stack direction="row" spacing={0.5}>
                                                {getDisplayTags(selectedTags).map((tag, index) => (
                                                    <Chip
                                                        key={index}
                                                        label={tag}
                                                        color={getTagColor(tag)}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                ))}
                                            </Stack>
                                        )}
                                        <IconButton
                                            onClick={handleTagEditClick}
                                            size="small"
                                            sx={{
                                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                "&:hover": {
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                                },
                                            }}
                                        >
                                            <Iconify
                                                icon="solar:pen-2-bold"
                                                sx={{ fontSize: 16, color: "primary.main" }}
                                            />
                                        </IconButton>

                                        {/* Tag Selection Menu */}
                                        <Menu
                                            anchorEl={tagMenuAnchor}
                                            open={Boolean(tagMenuAnchor)}
                                            onClose={handleTagMenuClose}
                                            PaperProps={{
                                                sx: {
                                                    borderRadius: 2,
                                                    minWidth: 200,
                                                    boxShadow: `0 8px 32px ${alpha(theme.palette.grey[500], 0.2)}`,
                                                },
                                            }}
                                        >
                                            {AVAILABLE_TAGS.map((tag) => (
                                                <MenuItem
                                                    key={tag}
                                                    onClick={() => handleTagToggle(tag)}
                                                    sx={{
                                                        py: 1.5,
                                                        px: 2,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                    }}
                                                >
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <Chip
                                                            label={tag}
                                                            size="small"
                                                            color={getTagColor(tag)}
                                                            variant="outlined"
                                                        />
                                                    </Box>
                                                    {selectedTags.includes(tag) && (
                                                        <Iconify
                                                            icon="solar:check-circle-bold"
                                                            sx={{ fontSize: 16, color: "success.main" }}
                                                        />
                                                    )}
                                                </MenuItem>
                                            ))}
                                        </Menu>
                                    </Stack>
                                </Stack>
                                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                                    {isEdit
                                        ? t("modify_your_existing_prompt_configuration")
                                        : t("create_a_new_ai_prompt_for_your_specific_use_case")}
                                </Typography>
                            </Box>
                            {hasUnsavedChanges && (
                                <Box
                                    sx={{
                                        px: 2,
                                        py: 0.5,
                                        borderRadius: 1,
                                        backgroundColor: alpha(theme.palette.warning.main, 0.1),
                                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                                    }}
                                >
                                    <Typography variant="caption" sx={{ color: "warning.main", fontWeight: 500 }}>
                                        {t("unsaved_changes")}
                                    </Typography>
                                </Box>
                            )}
                        </Stack>
                    </Box>
                </Card>

                {/* Main Form Card */}
                <Card
                    sx={{
                        borderRadius: 3,
                        boxShadow: `0 8px 32px ${alpha(theme.palette.grey[500], 0.12)}`,
                        border: `1px solid ${alpha(theme.palette.grey[500], 0.08)}`,
                        overflow: "hidden",
                    }}
                >
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Box sx={{ p: 4 }}>
                            <Stack spacing={4}>
                                {/* Prompt Name Section */}
                                <Box>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                        <Box
                                            sx={{
                                                p: 0.5,
                                                borderRadius: 1,
                                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                            }}
                                        >
                                            <Iconify
                                                icon="solar:tag-bold"
                                                sx={{ fontSize: 16, color: "primary.main" }}
                                            />
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {t("prompt_name")}
                                        </Typography>
                                    </Stack>
                                    <RHFTextField
                                        name="name"
                                        placeholder={t("enter_a_descriptive_name_for_your_prompt")}
                                        fullWidth
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 2,
                                                backgroundColor: alpha(theme.palette.grey[500], 0.02),
                                                "&:hover": {
                                                    backgroundColor: alpha(theme.palette.grey[500], 0.04),
                                                },
                                                "&.Mui-focused": {
                                                    backgroundColor: "transparent",
                                                },
                                            },
                                        }}
                                    />
                                </Box>

                                <Divider sx={{ borderStyle: "dashed" }} />

                                {/* Prompt Content Section */}
                                <Box>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        sx={{ mb: 2 }}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Box
                                                sx={{
                                                    p: 0.5,
                                                    borderRadius: 1,
                                                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                                                }}
                                            >
                                                <Iconify
                                                    icon="solar:document-text-bold"
                                                    sx={{ fontSize: 16, color: "success.main" }}
                                                />
                                            </Box>
                                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                {t("prompt_content")}
                                            </Typography>
                                        </Stack>
                                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                            {watchedContent?.length || 0} {t("characters")}
                                        </Typography>
                                    </Stack>

                                    <RHFTextField
                                        name="prompt"
                                        placeholder={t("enter_your_detailed_prompt_instructions_here")}
                                        multiline
                                        rows={16}
                                        fullWidth
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 2,
                                                backgroundColor: alpha(theme.palette.grey[500], 0.02),
                                                "&:hover": {
                                                    backgroundColor: alpha(theme.palette.grey[500], 0.04),
                                                },
                                                "&.Mui-focused": {
                                                    backgroundColor: "transparent",
                                                },
                                            },
                                            "& .MuiInputBase-root": {
                                                alignItems: "flex-start",
                                                fontFamily: "Monaco, Consolas, 'Courier New', monospace",
                                                fontSize: "0.875rem",
                                                lineHeight: 1.6,
                                            },
                                        }}
                                    />

                                    {/* Content Helper Text */}
                                    <Box
                                        sx={{
                                            mt: 2,
                                            p: 2,
                                            borderRadius: 2,
                                            backgroundColor: alpha(theme.palette.info.main, 0.05),
                                            border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                                        }}
                                    >
                                        <Stack direction="row" spacing={1}>
                                            <Iconify
                                                icon="solar:info-circle-bold"
                                                sx={{ fontSize: 16, color: "info.main", mt: 0.2 }}
                                            />
                                            <Box>
                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: "info.main", fontWeight: 600 }}
                                                >
                                                    Tips for writing effective prompts:
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: "text.secondary", display: "block", mt: 0.5 }}
                                                >
                                                    • Be specific and clear about the desired outcome • Provide context
                                                    and examples when necessary • Define the role or persona for the AI
                                                    • Include any constraints or guidelines
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Box>
                                </Box>
                            </Stack>
                        </Box>

                        {/* Action Buttons */}
                        <Box
                            sx={{
                                p: 3,
                                backgroundColor: alpha(theme.palette.grey[500], 0.02),
                                borderTop: `1px solid ${alpha(theme.palette.grey[500], 0.08)}`,
                            }}
                        >
                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                <Button
                                    variant="outlined"
                                    onClick={handleBackClick}
                                    sx={{
                                        borderRadius: 2,
                                        px: 3,
                                        py: 1,
                                        borderColor: alpha(theme.palette.grey[500], 0.3),
                                        "&:hover": {
                                            borderColor: alpha(theme.palette.grey[500], 0.5),
                                            backgroundColor: alpha(theme.palette.grey[500], 0.05),
                                        },
                                    }}
                                >
                                    {t("cancel")}
                                </Button>
                                <LoadingButton
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    loading={isSubmitting || isLoading}
                                    startIcon={<SaveIcon />}
                                    sx={{
                                        borderRadius: 2,
                                        px: 4,
                                        py: 1,
                                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                        boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                                        "&:hover": {
                                            transform: "translateY(-1px)",
                                            boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                                        },
                                        transition: "all 0.3s ease",
                                    }}
                                >
                                    {isEdit ? "Update Prompt" : "Create Prompt"}
                                </LoadingButton>
                            </Stack>
                        </Box>
                    </FormProvider>
                </Card>

                {/* Preview Card for Edit Mode */}
                {/* {isEdit && currentPrompt && (
                    <Card
                        sx={{
                            mt: 3,
                            borderRadius: 3,
                            boxShadow: `0 4px 20px ${alpha(theme.palette.grey[500], 0.1)}`,
                            border: `1px solid ${alpha(theme.palette.grey[500], 0.08)}`,
                        }}
                    >
                        <Box sx={{ p: 3 }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                <Iconify icon="solar:history-bold" sx={{ fontSize: 20, color: "text.secondary" }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Original Version
                                </Typography>
                            </Stack>
                            <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                                Created: {new Date(currentPrompt.createdAt).toLocaleString()}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                                Last modified: {new Date(currentPrompt.updatedAt).toLocaleString()}
                            </Typography>
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    backgroundColor: alpha(theme.palette.grey[500], 0.05),
                                    border: `1px solid ${alpha(theme.palette.grey[500], 0.1)}`,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: "Monaco, Consolas, 'Courier New', monospace",
                                        fontSize: "0.825rem",
                                        lineHeight: 1.6,
                                        color: "text.secondary",
                                        whiteSpace: "pre-wrap",
                                    }}
                                >
                                    {currentPrompt.content}
                                </Typography>
                            </Box>
                        </Box>
                    </Card>
                )} */}
            </Container>
        </Page>
    );
};

export default PromptsForm;
